import { call, put, select } from 'redux-saga/effects';

import { goToBoard, goToProject } from './router';
import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import { createLocalId } from '../../../utils/local-id';
import orm from '../../../orm';
import ActionTypes from '../../../constants/ActionTypes';

export function* createBoard(projectId, { import: boardImport, ...data }) {
  const nextData = {
    ...data,
    position: yield select(selectors.selectNextBoardPosition, projectId),
  };

  const localId = yield call(createLocalId);

  yield put(
    actions.createBoard({
      ...nextData,
      projectId,
      id: localId,
    }),
  );

  let board;
  let boardMemberships;

  try {
    ({
      item: board,
      included: { boardMemberships },
    } = yield boardImport
      ? call(
          request,
          api.createBoardWithImport,
          projectId,
          {
            ...nextData,
            importType: boardImport.type,
            importFile: boardImport.file,
          },
          localId,
        )
      : call(request, api.createBoard, projectId, nextData));
  } catch (error) {
    yield put(actions.createBoard.failure(localId, error));
    return;
  }

  yield put(actions.createBoard.success(localId, board, boardMemberships));
  yield call(goToBoard, board.id);
}

export function* createBoardInCurrentProject(data) {
  const { projectId } = yield select(selectors.selectPath);

  yield call(createBoard, projectId, data);
}

export function* handleBoardCreate(board, requestId) {
  const isExists = yield select(selectors.selectIsBoardWithIdExists, requestId);

  if (!isExists) {
    yield put(actions.handleBoardCreate(board));
  }
}

export function* fetchBoard(id) {
  console.log('[fetchBoard] Начало загрузки доски, id:', id);
  yield put(actions.fetchBoard(id));

  let board;
  let users;
  let projects;
  let boardMemberships;
  let labels;
  let lists;
  let cards;
  let cardMemberships;
  let cardLabels;
  let tasks;
  let attachments;

  let pointSystems;
  try {
    ({
      item: board,
      included: {
        users,
        projects,
        boardMemberships,
        labels,
        lists,
        cards,
        cardMemberships,
        cardLabels,
        tasks,
        attachments,
        cardPointData,
        cardPointAdvancedData,
        cardPointArchivedData,
        listReportData,
        contentCardDefaults,
        pointSystems: pointSystemsFromServer,
      },
    } = yield call(request, api.getBoard, id, true));
    pointSystems = pointSystemsFromServer || [];

    console.log('[fetchBoard] getBoard included lengths:', {
      users: users?.length || 0,
      projects: projects?.length || 0,
      boardMemberships: boardMemberships?.length || 0,
      labels: labels?.length || 0,
      lists: lists?.length || 0,
      cards: cards?.length || 0,
      cardMemberships: cardMemberships?.length || 0,
      cardLabels: cardLabels?.length || 0,
      tasks: tasks?.length || 0,
      attachments: attachments?.length || 0,
      cardPointData: cardPointData?.length || 0,
      cardPointAdvancedData: cardPointAdvancedData?.length || 0,
      cardPointArchivedData: cardPointArchivedData?.length || 0,
      listReportData: listReportData?.length || 0,
      contentCardDefaults: contentCardDefaults?.length || 0,
      pointSystems: pointSystems?.length || 0,
    });
  } catch (error) {
    yield put(actions.fetchBoard.failure(id, error));
    return;
  }

  // Сохраняем pointSystems в Redux перед сохранением board
  console.log('[fetchBoard] Сохраняем pointSystems:', pointSystems);
  if (pointSystems && pointSystems.length > 0) {
    for (const pointSystem of pointSystems) {
      console.log('[fetchBoard] Сохраняем pointSystem в Redux:', pointSystem);
      yield put(actions.updatePointSystem.success(pointSystem));
    }
  }

  console.log('[fetchBoard] Вызываем fetchBoard.success');
  yield put(
    actions.fetchBoard.success(
      board,
      users,
      projects,
      boardMemberships,
      labels,
      lists,
      cards,
      cardMemberships,
      cardLabels,
      tasks,
      attachments,
      cardPointData || [],
      listReportData || [],
    ),
  );
  console.log('[fetchBoard] fetchBoard.success вызван');

  // Загружаем DampingCoefficients СРАЗУ после сохранения данных в Redux,
  // чтобы pointSystem был доступен, но до того как компоненты начнут использовать селекторы
  // Это гарантирует, что DampingCoefficients будут загружены до отрисовки Card и List
  // Используем pointSystem из массива pointSystems, так как selectPointSystemForCurrentBoard может не найти его сразу
  let pointSystem = null;
  if (pointSystems && pointSystems.length > 0 && board) {
    pointSystem = pointSystems.find((ps) => String(ps.boardId) === String(board.id));
  }
  
  // Также пытаемся найти через селектор (на случай, если связь уже установлена)
  if (!pointSystem) {
    pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
  }
  
  console.log('[fetchBoard] После fetchBoard.success, pointSystem из массива:', pointSystems?.find((ps) => String(ps.boardId) === String(board.id)));
  console.log('[fetchBoard] После fetchBoard.success, pointSystem из селектора:', pointSystem);
  
  if (pointSystem) {
    const pointSystemId =
      pointSystem.id || (pointSystems?.find((ps) => String(ps.boardId) === String(board.id))?.id);
    console.log('[fetchBoard] Загружаем данные для подсчета баллов для pointSystemId:', pointSystemId);
    if (pointSystemId) {
      // Загружаем DampingCoefficients
      const dampingCoefficientsService = yield import('./damping-coefficients');
      yield call(dampingCoefficientsService.fetchDampingCoefficientsInCurrentBoard, pointSystemId);
      
      // Загружаем ContentViewPlans для расчета коэффициентов
      const contentViewPlansService = yield import('./content-view-plans');
      yield call(contentViewPlansService.fetchContentViewPlansInCurrentBoard, pointSystemId);
      
      // Загружаем EmployeePlans для расчета коэффициентов
      const employeePlansService = yield import('./employee-plans');
      yield call(employeePlansService.fetchEmployeePlansInCurrentBoard, pointSystemId);
      
      // Проверяем, что данные действительно загружены в ORM
      const session = orm.session(yield select(state => state.orm));
      const dampingCoefficientsInOrm = session.DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toRefArray();
      console.log('[fetchBoard] DampingCoefficients в ORM после загрузки:', dampingCoefficientsInOrm.length);
      
      const contentViewPlansInOrm = session.ContentViewPlan.all()
        .filter({ pointSistemId: pointSystemId })
        .toRefArray();
      console.log('[fetchBoard] ContentViewPlans в ORM после загрузки:', contentViewPlansInOrm.length);
      
      const employeePlansInOrm = session.EmployeePlan.all()
        .filter({ pointSystemId: pointSystemId })
        .toRefArray();
      console.log('[fetchBoard] EmployeePlans в ORM после загрузки:', employeePlansInOrm.length);
    }
  } else {
    console.log('[fetchBoard] pointSystem не найден, пропускаем загрузку данных для подсчета баллов');
  }

  // Обрабатываем cardPointData для детекции закрытости задач
  if (cardPointData && cardPointData.length > 0 && pointSystem) {
    console.log('[fetchBoard] dispatch CardPointData -> ORM:', {
      pointSystemId: pointSystem.id,
      count: cardPointData.length,
    });
    for (const item of cardPointData) {
      if (item) {
        yield put(actions.fetchCardPointData.success(item.cardId, pointSystem.id, item));
      }
    }
  } else {
    console.log('[fetchBoard] no CardPointData dispatch', {
      hasPointSystem: !!pointSystem,
      cardPointDataCount: cardPointData?.length || 0,
    });
  }

  // Обрабатываем cardPointAdvancedData для подсчета суммы баллов
  if (cardPointAdvancedData && cardPointAdvancedData.length > 0 && pointSystem) {
    // Группируем по cardId для обработки
    const cardPointAdvancedDataByCardId = {};
    cardPointAdvancedData.forEach((item) => {
      if (!cardPointAdvancedDataByCardId[item.cardId]) {
        cardPointAdvancedDataByCardId[item.cardId] = [];
      }
      cardPointAdvancedDataByCardId[item.cardId].push(item);
    });

    // Обрабатываем каждую карточку
    console.log('[fetchBoard] dispatch CardPointAdvancedData -> ORM:', {
      pointSystemId: pointSystem.id,
      cardsWithAdvanced: Object.keys(cardPointAdvancedDataByCardId).length,
      itemsTotal: cardPointAdvancedData.length,
    });
    for (const cardId of Object.keys(cardPointAdvancedDataByCardId)) {
      const items = cardPointAdvancedDataByCardId[cardId];
      yield put(actions.fetchCardPointAdvancedData.success(cardId, pointSystem.id, items));
    }
  } else {
    console.log('[fetchBoard] no CardPointAdvancedData dispatch', {
      hasPointSystem: !!pointSystem,
      cardPointAdvancedDataCount: cardPointAdvancedData?.length || 0,
    });
  }

  // Обрабатываем cardPointArchivedData для проверки автоматического закрытия месяца
  if (cardPointArchivedData && cardPointArchivedData.length > 0) {
    for (const item of cardPointArchivedData) {
      if (item) {
        // Используем action для создания/обновления CardPointArchivedData
        // Это тот же action, что используется при закрытии задачи
        yield put({
          type: ActionTypes.CARD_POINT_DATA_COMPLETE__SUCCESS,
          payload: {
            archivedItem: item,
          },
        });
      }
    }
  }

  // Обрабатываем listReportData для отображения статуса закрытия месяца
  if (listReportData && listReportData.length > 0) {
    console.log('[fetchBoard] dispatch ListReportData -> ORM:', {
      count: listReportData.length,
      closedCount: listReportData.filter((x) => x?.closed === true).length,
    });
    for (const item of listReportData) {
      if (item) {
        yield put(actions.fetchListReportData.success(item.listId, item));
      }
    }
  } else {
    console.log('[fetchBoard] no ListReportData dispatch', { listReportDataCount: listReportData?.length || 0 });
  }

  // Обрабатываем contentCardDefaults для подсчета суммы баллов
  if (pointSystem && contentCardDefaults && contentCardDefaults.length > 0) {
    console.log('[fetchBoard] dispatch ContentCardDefaults -> ORM:', {
      pointSystemId: pointSystem.id,
      count: contentCardDefaults.length,
    });
    yield put(actions.fetchContentCardDefaults.success(pointSystem.id, contentCardDefaults));
  } else {
    console.log('[fetchBoard] no ContentCardDefaults dispatch', {
      hasPointSystem: !!pointSystem,
      contentCardDefaultsCount: contentCardDefaults?.length || 0,
    });
  }
}

export function* updateBoard(id, data) {
  yield put(actions.updateBoard(id, data));

  let board;
  try {
    ({ item: board } = yield call(request, api.updateBoard, id, data));
  } catch (error) {
    yield put(actions.updateBoard.failure(id, error));
    return;
  }

  yield put(actions.updateBoard.success(board));
}

export function* handleBoardUpdate(board) {
  yield put(actions.handleBoardUpdate(board));
}

export function* moveBoard(id, index) {
  const { projectId } = yield select(selectors.selectBoardById, id);
  const position = yield select(selectors.selectNextBoardPosition, projectId, index, id);

  yield call(updateBoard, id, {
    position,
  });
}

export function* deleteBoard(id) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.deleteBoard(id));

  let board;
  try {
    ({ item: board } = yield call(request, api.deleteBoard, id));
  } catch (error) {
    yield put(actions.deleteBoard.failure(id, error));
    return;
  }

  yield put(actions.deleteBoard.success(board));
}

export function* handleBoardDelete(board) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (board.id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.handleBoardDelete(board));
}

export function* archiveBoard(id) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.archiveBoard(id));

  let board;
  try {
    ({ item: board } = yield call(request, api.archiveBoard, id));
  } catch (error) {
    yield put(actions.archiveBoard.failure(id, error));
    return;
  }

  yield put(actions.archiveBoard.success(board));
}

export function* handleBoardArchive(board) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (board.id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.handleBoardArchive(board));
}


export function* restoreBoard(id) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.restoreBoard(id));

  let board;
  try {
    ({ item: board } = yield call(request, api.restoreBoard, id));
  } catch (error) {
    yield put(actions.restoreBoard.failure(id, error));
    return;
  }

  yield put(actions.restoreBoard.success(board));
}

export function* handleBoardRestore(board) {
  const { boardId, projectId } = yield select(selectors.selectPath);

  if (board.id === boardId) {
    yield call(goToProject, projectId);
  }

  yield put(actions.handleBoardRestore(board));
}

export default {
  createBoard,
  createBoardInCurrentProject,
  handleBoardCreate,
  fetchBoard,
  updateBoard,
  handleBoardUpdate,
  moveBoard,
  deleteBoard,
  handleBoardDelete,
  archiveBoard,
  handleBoardArchive,
  restoreBoard,
  handleBoardRestore,
};
